/**
 * @fileoverview Report section template-adherence resolution (MOR-756).
 *
 * A section's `adherence` decides how the section writer treats the template:
 * - "strict"    — the guidance IS the section's standard wording. The writer
 *                 transcribes it verbatim, filling only {{field}} placeholders
 *                 from case data. No invented paragraphs, headings, tables,
 *                 citations or charts.
 * - "adaptable" — the guidance is still the standard wording, but the writer
 *                 may adapt grammar, pluralisation, and repetition (e.g. two
 *                 directors, a repeated block per asset) as directed by the
 *                 section's `adaptationNotes`. Everything the notes don't
 *                 require adapting stays word-for-word.
 * - "flexible"  — the guidance describes what the section should contain and
 *                 the writer drafts it from the analysed case data.
 *
 * The field is optional (older templates predate it), and practitioners had
 * already been pasting "FIXED SECTION — reproduce EXACTLY…" style instructions
 * into guidance to try to lock sections down. `resolveAdherence` honours the
 * explicit field first and falls back to detecting that style of guidance, so
 * existing templates get strict handling without being re-scanned or re-edited.
 *
 * Dependency-free on purpose: imported by Convex functions AND the template
 * editor UI.
 */

export type SectionAdherence = "strict" | "adaptable" | "flexible";

/** True when the section's guidance is standard wording (strict or adaptable). */
export function isTemplateWording(adherence: SectionAdherence): boolean {
  return adherence === "strict" || adherence === "adaptable";
}

/**
 * Signals that guidance is standard wording to reproduce, not a content brief.
 * Matched case-insensitively against the guidance text.
 */
const STRICT_GUIDANCE_SIGNALS: RegExp[] = [
  /\bfixed section\b/i,
  /\bverbatim\b/i,
  /\bstandard wording\b/i,
  /\breproduce\b[\s\S]{0,80}\bexactly\b/i,
  /\bexactly as written\b/i,
  /\bword[- ]for[- ]word\b/i,
  /\bdo(?:es)? not change between matters\b/i,
  /\bmust (?:remain|stay) (?:unchanged|identical)\b/i,
];

/** True when guidance reads as locked standard wording (legacy templates). */
export function detectStrictGuidance(guidance: string | undefined): boolean {
  if (!guidance) return false;
  return STRICT_GUIDANCE_SIGNALS.some((re) => re.test(guidance));
}

/**
 * Effective adherence for a section: the explicit field when set, otherwise
 * inferred from the guidance text.
 */
export function resolveAdherence(section: {
  adherence?: SectionAdherence;
  guidance?: string;
}): SectionAdherence {
  if (section.adherence) return section.adherence;
  return detectStrictGuidance(section.guidance) ? "strict" : "flexible";
}

/**
 * Two-slot wording model (MOR-756 follow-up).
 *
 * Every generate section keeps two independent texts and swaps the visible
 * `guidance` between them as `adherence` changes:
 * - `sourceWording`        — verbatim wording lifted from the uploaded report.
 *                            Shown for strict / adaptable (Exact / Adaptable).
 * - `draftingInstructions` — an AI-drafted content brief describing what the
 *                            section should contain. Shown for flexible
 *                            (AI drafted).
 *
 * `guidance` always mirrors the slot for the section's current adherence, so
 * the section writer and the editor can keep reading a single field. Toggling
 * adherence snapshots the visible guidance back into its slot (preserving
 * edits) before swapping in the other slot.
 */

/** In-flight / failed pull of the wording slots from the uploaded report. */
export type SourceWordingStatus = "extracting" | "failed";

/** The stored wordings + active guidance a template section can hold. */
export type SectionWordingState = {
  fillMode: "generate" | "attachment";
  adherence?: SectionAdherence;
  guidance?: string;
  sourceWording?: string;
  draftingInstructions?: string;
  sourceWordingStatus?: SourceWordingStatus;
};

/** The mode-specific slot the active guidance belongs to. */
export function activeWordingSlot(
  adherence: SectionAdherence,
): "sourceWording" | "draftingInstructions" {
  return isTemplateWording(adherence)
    ? "sourceWording"
    : "draftingInstructions";
}

const hasText = (value: string | undefined): value is string =>
  value !== undefined && value.trim() !== "";

/**
 * True when the two slots hold identical text. That only happens when a brief
 * was mistakenly snapshotted into `sourceWording` (legacy data): verbatim
 * wording and a content brief are never the same, so this flags the wording
 * slot as polluted and in need of a fresh transcription from the report.
 */
export function isWordingSlotPolluted(section: SectionWordingState): boolean {
  return (
    hasText(section.sourceWording) &&
    hasText(section.draftingInstructions) &&
    section.sourceWording.trim() === section.draftingInstructions.trim()
  );
}

/**
 * True when a generate section is missing either wording slot (or its wording
 * slot is polluted, see {@link isWordingSlotPolluted}) and has some base text
 * to derive it from.
 *
 * Sections in an `extracting` state are skipped unless `retryFailed` is set and
 * they previously failed.
 */
export function needsTemplateWordingFill(
  section: SectionWordingState,
  opts?: { retryFailed?: boolean },
): boolean {
  if (section.fillMode === "attachment") return false;
  if (section.sourceWordingStatus === "extracting") return false;
  if (section.sourceWordingStatus === "failed" && opts?.retryFailed !== true) {
    return false;
  }
  const hasBase =
    hasText(section.guidance) ||
    hasText(section.sourceWording) ||
    hasText(section.draftingInstructions);
  if (!hasBase) return false;
  return (
    !hasText(section.sourceWording) ||
    !hasText(section.draftingInstructions) ||
    isWordingSlotPolluted(section)
  );
}

/** Patch produced by {@link planAdherenceChange}. */
export type AdherenceChangePatch = {
  adherence: SectionAdherence;
  guidance?: string;
  sourceWording?: string;
  draftingInstructions?: string;
  sourceWordingStatus?: SourceWordingStatus | undefined;
};

/** Plan for switching a section between wording modes. */
export type AdherenceChangePlan = {
  patch: AdherenceChangePatch;
  /** True when the target slot is empty and must be filled from the report. */
  needsFill: boolean;
};

/**
 * Plan the field changes for moving a section from its current adherence to
 * `next`. Snapshots the visible guidance into the current mode's slot, then
 * swaps in the target slot (or flags a fill when it is empty).
 *
 * @example
 * const plan = planAdherenceChange(section, "flexible");
 * await ctx.db.patch("reportTemplateSections", section._id, plan.patch);
 * if (plan.needsFill) // schedule the wording pull
 */
export function planAdherenceChange(
  section: SectionWordingState,
  next: SectionAdherence,
): AdherenceChangePlan {
  const current = resolveAdherence(section);
  const guidance = section.guidance ?? "";
  const currentWording = isTemplateWording(current);
  const nextWording = isTemplateWording(next);

  // Snapshot the visible guidance into the slot for the CURRENT mode so edits
  // survive the swap.
  const snapshot: Pick<
    AdherenceChangePatch,
    "sourceWording" | "draftingInstructions"
  > = currentWording
    ? { sourceWording: guidance }
    : { draftingInstructions: guidance };

  // strict <-> adaptable share the wording slot: nothing to swap or fetch.
  if (currentWording === nextWording) {
    return { patch: { adherence: next, ...snapshot }, needsFill: false };
  }

  const target = nextWording
    ? section.sourceWording
    : section.draftingInstructions;

  if (hasText(target)) {
    return {
      patch: {
        adherence: next,
        ...snapshot,
        guidance: target,
        sourceWordingStatus: undefined,
      },
      needsFill: false,
    };
  }

  // Target slot empty — fill it from the uploaded report.
  return {
    patch: { adherence: next, ...snapshot, sourceWordingStatus: "extracting" },
    needsFill: true,
  };
}
